import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  Signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { ButtonComponent, IconButtonComponent } from '@libs/common/components';
import { QuantityCounterComponent } from '@libs/form/components';
import { QuantityCounterInputModel } from '@libs/form/models';
import { ProductModel } from '@libs/shop/models';
import { AddToCartActionData, CartQueueItem, CartService } from '@libs/shared/services';
import { CartTrackingService, ProductTrackingListDirective } from '@libs/shared/tracking';
import { Observable, switchMap } from 'rxjs';
import { ProductCounterData } from './add-to-cart.form.model';
import { AddToCartFormResult } from './add-to-cart.model';

@Component({
  selector: 'ui-add-to-cart',
  templateUrl: './add-to-cart.component.html',
  styleUrls: ['./add-to-cart.component.scss'],
  imports: [
    ReactiveFormsModule,
    QuantityCounterComponent,
    MatButtonModule,
    ButtonComponent,
    IconButtonComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddToCartComponent {
  public readonly sku: InputSignal<string> = input.required();
  public readonly sku$: Observable<string> = toObservable(this.sku);
  public product: InputSignal<ProductModel> = input.required();
  public position: InputSignal<number> = input.required();

  public defaultAmount: InputSignal<number> = input<number>(1);
  public showAddToShoppingList: InputSignal<boolean> = input<boolean>(false);
  public showButtonLabels: InputSignal<boolean> = input<boolean>(false);
  public showQuantityCounter: InputSignal<boolean> = input<boolean>(true);
  public orderButtonDisabled: InputSignal<boolean> = input<boolean>(false);
  public addToShoppingButtonListDisabled: InputSignal<boolean> = input<boolean>(false);
  public quantityCounterDisabled: InputSignal<boolean> = input<boolean>(false);
  public maxOrderAmount: InputSignal<number | undefined> = input<number | undefined>(undefined);
  public addToShoppingListClicked: OutputEmitterRef<AddToCartFormResult> = output<AddToCartFormResult>();

  public dataQuantityCounter: QuantityCounterInputModel = ProductCounterData;
  public quantityCounterControl: UntypedFormControl = new UntypedFormControl();
  public group: FormGroup = new UntypedFormGroup({
    [this.dataQuantityCounter.name]: this.quantityCounterControl,
  });

  public readonly cartQueue: Signal<CartQueueItem[]> = toSignal(this.cartService.getCartQueue(), { initialValue: [] });

  public inCartCount: Signal<number> = toSignal(
    this.sku$.pipe(switchMap((sku) => this.cartService.productInCartCount(sku))),
    { initialValue: 0 },
  );

  public inCartCountView: Signal<string> = toSignal(
    this.sku$.pipe(switchMap((sku) => this.cartService.getProductInCartCountView(sku))),
    { initialValue: '' },
  );

  public isAddToCartLoading: Signal<boolean> = computed(() => this.cartQueue()
    .filter((item) => item.actionType === 'add')
    .findIndex((item) => (item.data as AddToCartActionData).sku === this.sku()) > -1);

  private readonly cartTrackingService: CartTrackingService = inject(CartTrackingService);
  private readonly productTrackingList: ProductTrackingListDirective | null = inject(ProductTrackingListDirective, { optional: true });

  public constructor(private readonly cartService: CartService) {
    // Effect to handle defaultAmount changes
    effect(() => {
      this.dataQuantityCounter = {
        ...ProductCounterData,
        value: this.defaultAmount(),
      };
      this.quantityCounterControl.setValue(this.dataQuantityCounter.value);
    });

    // Effect to handle quantityCounterDisabled changes
    effect(() => {
      this.dataQuantityCounter.attributes = {
        ...this.dataQuantityCounter.attributes,
        disabled: this.quantityCounterDisabled(),
      };
    });

    // Effect to handle maxOrderAmount changes
    effect(() => {
      const maxAmount: number | undefined = this.maxOrderAmount();

      this.dataQuantityCounter.attributes = {
        ...this.dataQuantityCounter.attributes,
        max: maxAmount ? maxAmount.toString() : undefined,
      };
    });
  }

  public onSubmit(): void {
    if (!this.group.valid && !this.group.disabled) {
      return;
    }

    const quantity: number = (this.group.value as AddToCartFormResult).productQuantity;

    void this.cartService.queueCartItem('add', {
      sku: this.sku(),
      quantity: quantity,
    });

    this.cartTrackingService.trackAddToCartProducts([
      {
        ...this.product(),
        type: 'product',
        sku: this.sku(),
        position: this.position(),
        quantity: quantity,
      },
    ],
    this.productTrackingList?.trackingList());
  }
}
